import * as angular from 'angular';
import * as _ from 'lodash';

import { IApprovalStrategyService } from './';
import { IRequestService } from '../request.service';
import {
    ApprovalDetails,
    ApprovalStatus,
    DataToApprove,
    Dictionary,
    IApproval,
    IApprovalHistoryDto,
    IApprovalSeries,
    IApprovalStatusDto,
    IApprovalStrategy,
    IGuidsAndDates,
    ISaveApprovalStatusDto,
    JustificationLevel
} from '../../models';
import { GlobalApprovalStatusDto } from '../../models/approval/GlobalApprovalStatusDto';

export interface GroupConfig {
    isFutureAllowed: boolean;
    maxLevel: number;
    strategyName: string;
    userLevel: number[];
}

export interface ApprovalTableConfigReadTuple {
    hasAlarm?:boolean;
    hasQuality: boolean;
    isTreeMode: boolean;
    readTuples: any[];
}

export interface IApprovalTableConfigForStrategy {
    approvalValuesReadTuple: ApprovalTableConfigReadTuple;
    identifierNames: any[];
    rows: any[];
}

export interface IGroupApprovalService {
    computeApprovalsUsingHistory(maxLevel: number, history: IApprovalHistoryDto[], strategyGuid: string, strategies: IApprovalStrategy[]): IApproval[];
    getApprovalDetails(groupAndDates: IGuidsAndDates): angular.IPromise<Dictionary<ApprovalDetails[]>>;
    getConfigurationForStrategy(strategyGuid: string): angular.IPromise<IApprovalTableConfigForStrategy>;
    getDetails(guid: string, beginDate: string, endDate: string, config: ApprovalTableConfigReadTuple): angular.IPromise<IApprovalStatusDto[]>;
    getGlobalStatuses(beginDateTime: string, endDateTime: string, approvalStrategyGuid: string, force?: boolean): angular.IPromise<GlobalApprovalStatusDto>;
    getGroupConfig(groupGuid: string): angular.IPromise<GroupConfig>;
    getGroupHistoriesForDates(groupGuid: string, dates: string[]): angular.IPromise<{ [date: string]: IApprovalHistoryDto[] }>;
    getGroupHistoryAtDate(groupGuid: string, date: string): angular.IPromise<IApprovalHistoryDto[]>;
    getSettings(): angular.IPromise<{ series: IApprovalSeries[] }>;
    getStatuses(beginDate: string, endDate: string, config: any): angular.IPromise<IApprovalStatusDto[]>;
    isDatesApproved(groupAndDates: IGuidsAndDates): angular.IPromise<Dictionary<Date[]>>
    isJustificationRequired(level: number, status: ApprovalStatus, strategyGuid: string): angular.IPromise<boolean>;
    isJustificationRequiredFromList(dataToApproveList: DataToApprove[]): angular.IPromise<boolean>;
    isRangeApproved(groups: IGuidsAndDates, beginDate: string, endDate: string): angular.IPromise<Dictionary<Date[]>>;
    requiresJustification(level: number, status: ApprovalStatus, strategy: IApprovalStrategy): boolean;
    saveStatuses(statuses: ISaveApprovalStatusDto[]): angular.IPromise<string>;
    tagsApprovedDates(tagsAndDates: IGuidsAndDates): angular.IPromise<Dictionary<Date[]>>;
}

export class GroupApprovalService implements IGroupApprovalService {

    static serviceId = 'groupApprovalService';
    static urlBase = 'groupApproval/';
    static $inject = ['approvalStrategyService', 'requestService'];

    constructor(private _approvalStrategyService: IApprovalStrategyService,
        private _requestService: IRequestService) {
    }

    getStatuses(beginDate: string, endDate: string, config: any): angular.IPromise<IApprovalStatusDto[]> {
        return this._requestService.postAtV1(GroupApprovalService.urlBase,
            {
                beginDateTime: beginDate,
                endDateTime: endDate,
                configuration: config
            });
    }

    getDetails(groupGuid: string, beginDate: string, endDate: string, config: any): angular.IPromise<IApprovalStatusDto[]> {
        return this._requestService.postAtV1(`${GroupApprovalService.urlBase}${groupGuid}`,
            {
                beginDateTime: beginDate,
                endDateTime: endDate,
                configuration: config
            });
    }

    getSettings(): angular.IPromise<{ series: any[] }> {
        return this._requestService.getAtV1(GroupApprovalService.urlBase + 'getSettings');
    }

    getConfigurationForStrategy(strategyGuid: string): angular.IPromise<any> {
        return this._requestService.getAtV1(GroupApprovalService.urlBase + 'getTableConfiguration/' + strategyGuid);
    }

    saveStatuses(statuses: ISaveApprovalStatusDto[]): angular.IPromise<string> {
        const base = GroupApprovalService.urlBase;
        this._requestService.cleanCacheAtV1(base);
        return this._requestService.postAtV1<string>(GroupApprovalService.urlBase + 'saveStatuses', statuses)
            .then(data => {
                statuses.forEach(s => {
                    this._requestService.cleanCacheAtV1(`${base}${s.groupGuid}/history?date=${s.beginDate}`);
                });
                return data;
            });
    }

    isDatesApproved(groupAndDates: IGuidsAndDates): angular.IPromise<Dictionary<Date[]>> {
        return this._requestService.postAtV1(GroupApprovalService.urlBase + 'isDatesApproved', groupAndDates);
    }

    isRangeApproved(groups: IGuidsAndDates, beginDate: string, endDate: string): angular.IPromise<Dictionary<Date[]>> {
        return this._requestService.postAtV1(
            GroupApprovalService.urlBase + 'isRangeApproved',
            groups,
            { beginDateTime: beginDate, endDateTime: endDate }
        );
    }

    getApprovalDetails(groupAndDates: IGuidsAndDates): angular.IPromise<Dictionary<ApprovalDetails[]>> {
        return this._requestService.postAtV1(GroupApprovalService.urlBase + 'approvalDetails', groupAndDates);
    }

    getGlobalStatuses(beginDateTime: string, endDateTime: string, approvalStrategyGuid: string, force: boolean = false): angular.IPromise<GlobalApprovalStatusDto> {
        const url = `${GroupApprovalService.urlBase}getGlobalStatuses`;
        const params = {
            beginDateTime,
            endDateTime,
            approvalStrategyGuid
        };
        if (force) {
            this._requestService.cleanCacheAtV1(url, params);
        }
        return this._requestService.getAtV1(url, params);
    }

    tagsApprovedDates(tagsAndDates: IGuidsAndDates): angular.IPromise<{ [index: string]: Date[]; }> {
        return this._requestService.postAtV1(GroupApprovalService.urlBase + 'tagsApprovedDates', tagsAndDates);
    }

    getGroupConfig(groupGuid: string): angular.IPromise<GroupConfig> {
        return this._requestService.getAtV1(GroupApprovalService.urlBase + groupGuid + '/config');
    }

    getGroupHistoryAtDate(groupGuid: string, date: string): angular.IPromise<IApprovalHistoryDto[]> {
        const url = GroupApprovalService.urlBase + `${groupGuid}/history?date=${date}`;
        return this._requestService
            .getAtV1<IApprovalHistoryDto[]>(url)
            .finally(() => this._requestService.cleanCacheAtV1(url));
    }

    getGroupHistoriesForDates(groupGuid: string, dates: string[]): angular.IPromise<{ [date: string]: IApprovalHistoryDto[] }> {
        const url = GroupApprovalService.urlBase + `${groupGuid}/histories`;
        return this._requestService
            .postAtV1<{ [date: string]: IApprovalHistoryDto[] }>(url, dates)
            .finally(() => this._requestService.cleanCacheAtV1(url));
    }

    computeApprovalsUsingHistory(
        maxLevel: number,
        history: IApprovalHistoryDto[],
        strategyGuid: string,
        strategies: IApprovalStrategy[]
    ): IApproval[] {
        const result: IApproval[] = new Array(maxLevel + 1);
        const sorted = _.sortBy<IApprovalHistoryDto>(history, 'approvedDate');
        sorted.reverse();

        let previous: IApproval = undefined;
        for (let level = maxLevel; level >= 0; level--) {
            const current: IApproval = this.setResult(sorted, level, previous, result);
            if (current) {
                previous = current;
                sorted.shift();
                if (current.level === maxLevel && current.status === ApprovalStatus.Approved
                    && sorted.length && sorted[0].level === maxLevel) {
                    previous = sorted.shift() as any;
                }
            }
        }

        this.computeAbilities(result, strategyGuid, strategies);
        return result;
    }

    private setResult(sorted: IApprovalHistoryDto[], level: number, previous: IApproval, result: IApproval[]) {
        const isL1Rejected = sorted[0]
            && sorted[0].status === ApprovalStatus.Rejected
            && sorted[0].level === (level + 1);

        const current: IApproval = ((sorted.length > 0 && sorted[0].level === level) || isL1Rejected)
            ? sorted[0] as any
            : undefined;
        let approval: IApproval;
        if (current?.status === ApprovalStatus.Approved ||
            previous?.status === ApprovalStatus.WaitingForApproval ||
            previous?.status === ApprovalStatus.Approved) {
            approval = this.getApproved(level, current, previous);
        } else if (previous?.status === ApprovalStatus.Rejected || isL1Rejected) {
            approval = this.getRejected(level, current, previous);
        } else if (current?.status === ApprovalStatus.WaitingForApproval) {
            approval = this.getWaitingForApproval(level);
        } else {
            approval = this.getDefault(level);
        }
        result[level] = approval;
        return current;
    }

    private getDefault(level: number): IApproval {
        return {
            level: level,
            status: level === 0 ? ApprovalStatus.WaitingForApproval : ApprovalStatus.Waiting,
            approvedDate: '',
            user: '',
            canReject: false,
            comment: undefined
        };
    }

    private getWaitingForApproval(level: number): IApproval {
        return {
            level: level,
            status: ApprovalStatus.WaitingForApproval,
            approvedDate: '',
            user: '',
            canReject: false,
            comment: undefined
        };
    }

    private getRejected(level: number, current: IApproval, previous: IApproval) {
        const isPrev = previous?.status === ApprovalStatus.Rejected;
        return {
            level: level,
            status: ApprovalStatus.Rejected,
            approvedDate: isPrev ? previous.approvedDate : current.approvedDate,
            user: isPrev ? previous.user : current.user,
            canReject: false,
            comment: isPrev ? previous.comment : current.comment,
        };
    }

    private getApproved(level: number, current: IApproval, previous: IApproval): IApproval {
        const isCurrent = current?.status === ApprovalStatus.Approved;
        return {
            level: level,
            status: ApprovalStatus.Approved,
            approvedDate: isCurrent ? current.approvedDate : previous.approvedDate,
            user: isCurrent ? current.user : previous.user,
            canReject: false,
            comment: isCurrent ? current.comment : previous.comment,
        };
    }

    isJustificationRequired(level: number, status: ApprovalStatus, strategyGuid: string): angular.IPromise<boolean> {
        return this._approvalStrategyService
            .getAll()
            .then(strategies => this.checkJustificationRequired(strategyGuid, level, status, strategies));
    }

    isJustificationRequiredFromList(dataToApproveList: DataToApprove[]): angular.IPromise<boolean> {
        return this._approvalStrategyService
            .getAll()
            .then(strategies => _.some(dataToApproveList, dta =>
                this.checkJustificationRequired(dta.groupStrategyGuid, dta.level, dta.status, strategies)
            ));
    }

    requiresJustification(level: number, status: ApprovalStatus, strategy: IApprovalStrategy): boolean {
        let required = false;
        const strategyLevel = _.find(strategy.approbationStrategyLevelCollection, l => l.level === level);
        if (strategyLevel) {
            required = strategyLevel.justificationLevel === JustificationLevel.Always
                || (strategyLevel.justificationLevel === JustificationLevel.OnApproval && status === ApprovalStatus.Approved)
                || (strategyLevel.justificationLevel === JustificationLevel.OnRejection && status === ApprovalStatus.Rejected);
        }
        return required;
    }

    private checkJustificationRequired(
        strategyGuid: string,
        level: number,
        status: ApprovalStatus,
        strategies: IApprovalStrategy[]
    ): boolean {
        let required = false;
        const s = _.find(strategies, st => st.guid === strategyGuid);
        if (s) {
            required = this.requiresJustification(level, status, s);
        }
        return required;
    }

    private computeAbilities(approvals: IApproval[], strategyGuid: string, strategies: IApprovalStrategy[]): void {
        //this._approvalStrategyService.getAll()
        //    .then(strategies => {
        const found = _.find(strategies, s => s.guid === strategyGuid);
        const strategyOk = found && found.undoApproval;

        approvals.forEach((a, i, arr) => {
            // can reject if
            // - we're last level and approved
            // - next level is not approved and strategy allow it
            const nextLevelNotApproved = arr[i + 1] && arr[i + 1].status === ApprovalStatus.WaitingForApproval;
            const isLastLevelAndApproved = (i + 1 === arr.length && a.status === ApprovalStatus.Approved);
            a.canReject = isLastLevelAndApproved || (strategyOk && nextLevelNotApproved);
        });
        //   });
    }
}